import { onMounted, onUnmounted } from 'vue'
import { KeepAwake } from '@capacitor-community/keep-awake'
import { Capacitor } from '@capacitor/core'

/**
 * Composable to keep the screen awake while the app is active
 *
 * Usage:
 * ```
 * // In your component
 * import { useKeepAwake } from '@/composables/useKeepAwake'
 *
 * // Keep screen awake for entire component lifecycle
 * useKeepAwake()
 * ```
 */
export function useKeepAwake() {
    let isAwake = false

    /**
     * Enable keep awake - prevents screen from locking
     */
    async function enableKeepAwake() {
        // Only run on native platforms (iOS/Android)
        if (Capacitor.getPlatform() === 'web') {
            console.log('[KeepAwake] Skipping - web platform detected')
            return
        }

        try {
            await KeepAwake.keepAwake()
            isAwake = true
            console.log('[KeepAwake] Screen keep-awake enabled')
        } catch (error) {
            console.error('[KeepAwake] Failed to enable keep-awake:', error)
        }
    }

    /**
     * Disable keep awake - allows screen to lock normally
     */
    async function disableKeepAwake() {
        // Only run on native platforms
        if (Capacitor.getPlatform() === 'web') {
            return
        }

        if (!isAwake) {
            return
        }

        try {
            await KeepAwake.allowSleep()
            isAwake = false
            console.log('[KeepAwake] Screen keep-awake disabled')
        } catch (error) {
            console.error('[KeepAwake] Failed to disable keep-awake:', error)
        }
    }

    // Automatically enable on mount and disable on unmount
    onMounted(async () => {
        await enableKeepAwake()
    })

    onUnmounted(async () => {
        await disableKeepAwake()
    })

    return {
        enableKeepAwake,
        disableKeepAwake,
        isAwake: () => isAwake
    }
}
